import { computed, ref, watch } from 'vue';
import { getBrowserStorage } from './tableScale.mjs';
import { createPreset, loadPresets, normalizePresets, savePresets, snapshotKey } from './filterPresetsState.mjs';

export function useFilterPresets({ storageKey, capture, apply }) {
  const storage = getBrowserStorage();
  const presets = ref(loadPresets(storage, storageKey));
  const lastAppliedId = ref(null);

  watch(presets, (value) => savePresets(storage, storageKey, normalizePresets(value)), { deep: true });

  const currentKey = computed(() => snapshotKey(capture()));
  const activeId = computed(() => {
    const key = currentKey.value;
    if (!key) return null;
    for (const p of presets.value) {
      if (snapshotKey(p.snapshot) === key) return p.id;
    }
    return null;
  });
  const dirtyId = computed(() => {
    if (!lastAppliedId.value) return null;
    return activeId.value === lastAppliedId.value ? null : lastAppliedId.value;
  });
  const defaultPreset = computed(() => presets.value.find((p) => p.isDefault) || null);

  const markApplied = (id) => { lastAppliedId.value = id; };

  const applyPreset = (id) => {
    const preset = presets.value.find((p) => p.id === id);
    if (!preset) return;
    apply(preset.snapshot);
    lastAppliedId.value = id;
  };

  const saveAs = (name) => {
    const trimmed = String(name ?? '').trim();
    if (!trimmed) return { ok: false, reason: 'empty' };
    const snapshot = capture();
    const existing = presets.value.find((p) => p.name.toLowerCase() === trimmed.toLowerCase());
    if (existing) {
      existing.snapshot = snapshot;
      apply(existing.snapshot);
      lastAppliedId.value = existing.id;
      return { ok: true, id: existing.id, overwritten: true };
    }
    const preset = createPreset(trimmed, snapshot);
    presets.value = [...presets.value, preset];
    apply(preset.snapshot);
    lastAppliedId.value = preset.id;
    return { ok: true, id: preset.id, overwritten: false };
  };

  const renamePreset = (id, name) => {
    const trimmed = String(name ?? '').trim();
    if (!trimmed) return false;
    const clash = presets.value.find((p) => p.id !== id && p.name.toLowerCase() === trimmed.toLowerCase());
    if (clash) return false;
    const preset = presets.value.find((p) => p.id === id);
    if (!preset) return false;
    preset.name = trimmed.slice(0, 60);
    return true;
  };

  const removePreset = (id) => {
    presets.value = presets.value.filter((p) => p.id !== id);
    if (lastAppliedId.value === id) lastAppliedId.value = null;
  };

  const reorderPresets = (idList) => {
    const map = new Map(presets.value.map((p) => [p.id, p]));
    const next = [];
    for (const id of idList) {
      const p = map.get(id);
      if (p) { next.push(p); map.delete(id); }
    }
    for (const p of map.values()) next.push(p);
    presets.value = next;
  };

  const setDefaultPreset = (id) => {
    presets.value = presets.value.map((p) => ({ ...p, isDefault: p.id === id }));
  };

  return {
    presets,
    activeId,
    dirtyId,
    defaultPreset,
    applyPreset,
    saveAs,
    renamePreset,
    removePreset,
    reorderPresets,
    setDefaultPreset,
    markApplied,
  };
}
